/*
 * File: LogoutAlertHandler.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the autologout alert with a countdown
 */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { LoginContext } from "../state/LoginProvider";

const LogoutAlertHandler = ({ children }) => {
  // provider state
  const { timer, countdown, dialog, login } = useContext(LoginContext);

  // flag used to determine alert visibility
  const timerDone = timer.done;

  // seconds countdown
  const count = countdown.count;

  // visibility state
  const [open, setOpen] = useState(timerDone);

  useEffect(() => {
    const init = () => {
      // alert is open so start the countdown
      if (open) countdown.start();

      // autologout timer has expired so stop the timer and show the alert
      if (timerDone) {
        timer.stop();
        setOpen(true);
      }

      // count is over so close the alert and show the login dialog
      if (count === 0) {
        countdown.reset();
        setOpen(false);
        login.setAutoLoggedOut();
        dialog.show();
      }
    };
    init();
  }, [count, timerDone, countdown, dialog, login, open, timer]);

  // handles the alert button click to close the alert and reset the counter
  const onClick = () => {
    countdown.reset();
    setOpen(false);
  };

  // props for the child component
  const childProps = {
    count,
    open,
    onClick
  };

  return React.cloneElement(children, childProps);
};

LogoutAlertHandler.propTypes = {
  children: PropTypes.node
};

export default LogoutAlertHandler;
